import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * La coda delle finestre che si aprono da sole (Alex, 1/10/2026).
 *
 * Fino a quel giorno la finestra dei compiti urgenti, quella delle
 * assicurazioni in scadenza e il pannello dei promemoria si aprivano
 * ognuna per conto suo, una sopra l'altra: la prima copriva i pulsanti
 * della seconda, e il pannello dell'angolo poteva coprire i pulsanti di
 * tutte e due. Una persona doveva indovinare quale chiudere.
 *
 * LA REGOLA: una finestra alla volta, nell'ordine di PRIORITA; la
 * successiva solo dopo che si e' chiusa quella prima. Il pannello dei
 * promemoria e la pila dei ritardi (l'angolo) aspettano che non ci sia
 * nessuna finestra aperta: cosi' non possono coprirne i pulsanti, per
 * costruzione e non per un calcolo di posizioni.
 *
 * Chi apre una finestra chiama useTurno(nome, haQualcosaDaMostrare) e si
 * disegna solo se gli torna true. L'ordine sta QUI e da nessun'altra parte.
 */

/* Prima i compiti urgenti: sono il lavoro di oggi, e qualcuno li aspetta.
   Poi le assicurazioni in scadenza: contano, ma hanno trenta giorni. */
export const PRIORITA = ['compiti', 'assicurazioni'];

export const CodaFinestre = createContext(null);

/** Lo stato della coda: lo tiene il Layout. */
export function useStatoCoda() {
    const [pronte, setPronte] = useState({});
    const segna = useCallback((nome, pronta) => {
        setPronte((p) => (!!p[nome] === !!pronta ? p : { ...p, [nome]: !!pronta }));
    }, []);
    const turno = PRIORITA.find((n) => pronte[n]) || null;
    /* Un oggetto nuovo solo quando cambia il turno: chi lo legge non deve
       rimettersi in moto a ogni disegno del Layout. */
    return useMemo(() => ({ segna, turno }), [segna, turno]);
}

/** true se tocca a questa finestra. Fuori dal Layout (nessuna coda) vale sempre. */
export function useTurno(nome, pronta) {
    const coda = useContext(CodaFinestre);
    /* Gli effetti dipendono da `segna`, che non cambia mai, e non dall'oggetto
       della coda: altrimenti a ogni cambio di turno la pulizia la
       ritirerebbe e l'effetto la rimetterebbe, un disegno dopo l'altro. */
    const segna = coda ? coda.segna : null;
    useEffect(() => { if (segna) segna(nome, pronta); }, [segna, nome, pronta]);
    useEffect(() => () => { if (segna) segna(nome, false); }, [segna, nome]);
    return !coda || coda.turno === nome;
}
